import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Barcode, ArrowCircleUp, ArrowCircleDown, ClipboardText } from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const ACTIONS = [
  { key: "carico", label: "Carico (Entrata)", icon: ArrowCircleDown, color: "#2A9D8F" },
  { key: "scarico", label: "Scarico (Uscita)", icon: ArrowCircleUp, color: "#E63946" },
  { key: "inventario", label: "Inventario (Rettifica)", icon: ClipboardText, color: "#F4A261" },
];

export default function Scanner() {
  const [action, setAction] = useState("carico");
  const [quantity, setQuantity] = useState(1);
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [last, setLast] = useState(null);
  const [flash, setFlash] = useState(""); // "success" | "error"
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    const h = setInterval(() => {
      if (document.activeElement !== inputRef.current) {
        inputRef.current?.focus();
      }
    }, 500);
    return () => clearInterval(h);
  }, []);

  const submit = async (barcode) => {
    if (!barcode) return;
    const qty = Number(quantity);
    if (!qty || qty < 0) {
      toast.error("Quantità non valida");
      return;
    }
    try {
      const { data } = await api.post("/movements/scan", {
        barcode,
        type: action,
        quantity: qty,
        note: note || null,
      });
      setLast({ ok: true, mov: data });
      setHistory((h) => [data, ...h].slice(0, 8));
      setFlash("success");
      toast.success(`${data.product_name}: ${action}`);
    } catch (err) {
      setLast({ ok: false, msg: formatApiError(err), barcode });
      setFlash("error");
      toast.error(formatApiError(err));
    } finally {
      setCode("");
      setTimeout(() => setFlash(""), 700);
      inputRef.current?.focus();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit(code.trim());
    }
  };

  const currentAction = ACTIONS.find((a) => a.key === action);

  return (
    <div className="p-8 lg:p-12 fade-up" data-testid="scanner-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">
          Registrazione con Pistola EAN-13
        </div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A]">
          Scanner
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-2">
            Operazione
          </label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger data-testid="scanner-action-select" className="rounded-none h-12">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTIONS.map((a) => (
                <SelectItem key={a.key} value={a.key}>
                  {a.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-2">
            Quantità
          </label>
          <input
            type="number"
            step="0.001"
            data-testid="scanner-quantity-input"
            className="w-full border border-[#E5E7EB] bg-white px-4 h-12 text-base outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-2">
            Nota (opz.)
          </label>
          <input
            data-testid="scanner-note-input"
            className="w-full border border-[#E5E7EB] bg-white px-4 h-12 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Es. ordine, fornitore…"
          />
        </div>
      </div>

      <div
        className={`bg-[#F9FAFB] border-2 border-dashed p-12 text-center mb-6 transition-colors ${
          flash === "success"
            ? "scan-flash-success"
            : flash === "error"
            ? "scan-flash-error"
            : "border-[#1D3557]"
        }`}
        data-testid="scanner-zone"
      >
        <div className="flex items-center justify-center gap-3 mb-5">
          <currentAction.icon size={24} weight="bold" color={currentAction.color} />
          <div className="font-display font-semibold uppercase tracking-wider text-sm text-[#0A0A0A]">
            Pronto · {currentAction.label}
          </div>
        </div>
        <Barcode size={64} weight="duotone" color="#1D3557" className="mx-auto mb-4" />
        <input
          ref={inputRef}
          data-testid="barcode-scanner-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Scansiona o digita EAN-13"
          className="w-full max-w-lg mx-auto text-3xl font-mono-data text-center border-b-4 border-[#1D3557] bg-transparent outline-none focus:ring-0 py-3 placeholder:text-gray-300"
          autoFocus
          autoComplete="off"
        />
        <div className="mt-4 text-xs text-[#4B5563] uppercase tracking-wider">
          Punta la pistola verso il codice a barre · invio automatico
        </div>
      </div>

      {last && (
        <div
          className={`border p-5 mb-6 ${
            last.ok
              ? "bg-[#E6F4F1] border-[#2A9D8F]/30"
              : "bg-[#FBEDEE] border-[#E63946]/30"
          }`}
          data-testid="scanner-last-result"
        >
          {last.ok ? (
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#2A9D8F] mb-1">
                  Registrato
                </div>
                <div className="font-display font-semibold text-lg text-[#0A0A0A]">
                  {last.mov.product_name}
                </div>
                <div className="font-mono-data text-xs text-[#4B5563]">
                  {last.mov.product_barcode}
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono-data text-2xl font-bold text-[#0A0A0A]">
                  {last.mov.quantity > 0 ? "+" : ""}
                  {last.mov.quantity} {last.mov.unit}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
                  Nuovo stock: {last.mov.stock_after} {last.mov.unit}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#E63946] mb-1">
                Errore
              </div>
              <div className="font-display font-semibold text-base text-[#0A0A0A]">
                {last.msg}
              </div>
              <div className="font-mono-data text-xs text-[#4B5563] mt-1">
                Codice: {last.barcode}
              </div>
            </div>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div className="bg-white border border-[#E5E7EB]" data-testid="scanner-history">
          <div className="p-5 border-b border-[#E5E7EB]">
            <div className="font-display font-semibold uppercase tracking-wider text-xs text-[#0A0A0A]">
              Ultime scansioni ({history.length})
            </div>
          </div>
          <ul>
            {history.map((m) => (
              <li
                key={m.id}
                className="p-4 border-b border-[#E5E7EB] last:border-b-0 flex items-center justify-between"
              >
                <div>
                  <div className="text-sm font-medium text-[#0A0A0A]">{m.product_name}</div>
                  <div className="font-mono-data text-[10px] text-[#4B5563]">
                    {m.product_barcode} · {m.type}
                  </div>
                </div>
                <div className="font-mono-data text-sm font-semibold text-[#0A0A0A]">
                  {m.quantity > 0 ? "+" : ""}
                  {m.quantity} {m.unit}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
